import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Chip,
  Stack,
  Tabs,
  Tab,
  Box,
  Typography,
  IconButton
} from '@mui/material';
import { Add as AddIcon, Close as CloseIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import ProceduralMemoryBox from '../Boxes/Memories/ProceduralMemoryBox';
import SemanticMemoryBox from '../Boxes/Memories/SemanticMemoryBox';
import WorkingMemoryBox from '../Boxes/Memories/WorkingMemoryBox';
import EpisodicMemoryBox from '../Boxes/Memories/EpisodicMemoryBox';
import AgentNode from '../../models/Nodes/AgentNode';
import SemanticMemory from '../../models/Memories/SemanticMemory';
import ProceduralMemory from '../../models/Memories/ProceduralMemory';
import { CustomNode } from '../../types/flow';
import { DEFAULT_MODEL, DEFAULT_TEMPERATURE } from '../../config/models';
import PineconeConnection from '../../models/Connections/PineconeConnection';
import AWSConnection from '../../models/Connections/AWSConnection';
import MongoDBConnection from '../../models/Connections/MongoDBConnection';

interface InputNodeModalProps {
  open: boolean;
  onClose: () => void;
  node?: CustomNode | null;
  onUpdate: (updatedNode: any) => void;
  workflowInputs: Record<string, any>;
  setWorkflowInputs?: (inputs: Record<string, any>) => void;
}

// Utility to ensure all connections are class instances
function ensureConnectionInstance(conn: any) {
  if (conn instanceof PineconeConnection || conn instanceof AWSConnection || conn instanceof MongoDBConnection) {
    return conn;
  }
  if (conn.type === 'pinecone') return new PineconeConnection(conn);
  if (conn.type === 'aws') return new AWSConnection(conn);
  if (conn.type === 'mongodb_json') return new MongoDBConnection(conn);
  throw new Error('Unknown connection type');
}

const InputNodeModal: React.FC<InputNodeModalProps> = ({
  open,
  onClose,
  node,
  onUpdate,
  workflowInputs,
  setWorkflowInputs
}) => {
  const [parameters, setParameters] = useState<string[]>([]);
  const [newParameter, setNewParameter] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [currentNode, setCurrentNode] = useState<AgentNode | null>(null);
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    if (node) {
      setParameters(node.data?.agent?.requiredParameters || []);
      setCurrentNode(node.data?.agent);
    } else {
      const newNode = new AgentNode(
        `input_${Date.now()}`,
        new SemanticMemory(0.1, []),
        new ProceduralMemory(),
        'input_node'
      );
      setCurrentNode(newNode);
    }
  }, [node]);

  const updateNode = (updatedNode: AgentNode, updatedParams: string[]) => {
    const nodeUpdate = {
      ...node, // Preserve all original node properties including position
      id: updatedNode.id,
      data: {
        ...node?.data, // Preserve existing data properties
        label: updatedNode.id,
        agent: {
          ...updatedNode,
          type: 'input_node',
          requiredParameters: updatedParams
        },
        type: 'input_node',
      }
    };
    setCurrentNode(updatedNode);
    onUpdate(nodeUpdate);
  };

  const handleAddParameter = () => {
    if (!newParameter.trim()) {
      enqueueSnackbar('Please enter a parameter name', { variant: 'error' });
      return;
    }
    if (parameters.includes(newParameter.trim())) {
      enqueueSnackbar('Parameter already exists', { variant: 'error' });
      return;
    }
    const updatedParams = [...parameters, newParameter.trim()];
    setParameters(updatedParams);
    setNewParameter('');
    if (currentNode) {
      updateNode(currentNode, updatedParams);
    }
  };

  const handleRemoveParameter = (param: string) => {
    const updatedParams = parameters.filter(p => p !== param);
    setParameters(updatedParams);
    if (currentNode) {
      updateNode(currentNode, updatedParams);
    }
  };

  const handleProceduralMemoryUpdate = (memory: ProceduralMemory) => {
    if (!currentNode) return;
    const updatedNode = new AgentNode(
      currentNode.id,
      currentNode.semanticMemory,
      memory,
      'input_node',
      null,
      null,
      [],
      undefined,
      parameters
    );
    setCurrentNode(updatedNode);
    updateNode(updatedNode, parameters);
    onUpdate({
      ...node,
      data: {
        ...node?.data,
        agent: updatedNode
      }
    });
  };

  const handleSemanticMemoryUpdate = (memory: SemanticMemory) => {
    if (!currentNode) return;
    const updatedNode = new AgentNode(
      currentNode.id,
      memory,
      currentNode.proceduralMemory,
      'input_node',
      null,
      null,
      [],
      undefined,
      parameters
    );
    setCurrentNode(updatedNode);
    updateNode(updatedNode, parameters);
    onUpdate({
      ...node,
      data: {
        ...node?.data,
        agent: updatedNode
      }
    });
  };

  const handleOk = () => {
    if (!currentNode) return;
    onClose();
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const tabLabels = ['Procedural Memory', 'Semantic Memory', 'Working Memory', 'Episodic Memory'];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>Input Node Configuration</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>Required Parameters</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Add mandatory parameters that this input node will process
          </Typography>

          <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
            <TextField
              placeholder="Enter parameter name"
              value={newParameter}
              onChange={(e) => setNewParameter(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleAddParameter()}
              size="small"
              fullWidth
            />
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddParameter}
              size="small"
            >
              Add
            </Button>
          </Stack>

          <Box sx={{ mb: 2 }}>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {parameters.map((param) => (
                <Chip
                  key={param}
                  label={param}
                  onDelete={() => handleRemoveParameter(param)}
                  deleteIcon={<CloseIcon />}
                  sx={{ mb: 1 }}
                />
              ))}
            </Stack>
            {parameters.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                At least one parameter is required
              </Typography>
            )}
          </Box>

          <Tabs value={activeTab} onChange={handleTabChange} sx={{ mb: 2 }}>
            {tabLabels.map((label, index) => (
              <Tab key={index} label={label} />
            ))}
          </Tabs>

          <Box sx={{ mt: 2 }}>
            {activeTab === 0 && (
              <ProceduralMemoryBox
                memory={currentNode?.proceduralMemory || new ProceduralMemory(DEFAULT_MODEL, DEFAULT_TEMPERATURE, [], {
                  learn: { enabled: false, steps: [] },
                  retrieve: { enabled: false, steps: [] },
                  reason: { enabled: false, steps: [] },
                  design: { enabled: false, steps: [] },
                  reflect: { enabled: false, steps: [] }
                }, '')}
                onMemoryChange={handleProceduralMemoryUpdate}
                workflowInputs={workflowInputs}
                setWorkflowInputs={setWorkflowInputs}
              />
            )}
            {activeTab === 1 && (
              <SemanticMemoryBox
                memory={(() => {
                  const mem = currentNode?.semanticMemory || new SemanticMemory(0.1, []);
                  const safeConnections = mem.connections.map(ensureConnectionInstance);
                  return new SemanticMemory(mem.similarityThreshold, safeConnections);
                })()}
                onMemoryChange={handleSemanticMemoryUpdate}
              />
            )}
            {activeTab === 2 && (
              <WorkingMemoryBox />
            )}
            {activeTab === 3 && (
              <EpisodicMemoryBox />
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleOk}
          disabled={parameters.length === 0}
        >
          OK
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InputNodeModal;
